import HeroBg from "@/containers/activities/stock-rebate/HeroBg";
import SpaceScene from "@/containers/activities/stock-rebate/SpaceScene";
import InviteBtn from "@/containers/activities/stock-rebate/InviteBtn";
import StockNavigation from "@/containers/activities/stock-rebate/StockNavigation";
import RFLayout from "@/layout/RFLayout";
import parseAcceptLang from "@/utils/parseAccptLang";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { ReactElement, useRef, useState } from "react";
import Image from "next/image";
import ContactUsFinished from "@/containers/activities/stock-rebate/ContactUsFinished";
import getFullWebCdnUrl from "@/utils/getFullWebCdnUrl";
import MarqueeView from "@/containers/activities/stock-rebate/MarqueeView";
import AVDView from "@/containers/activities/stock-rebate/AVDView";
import PullNewServices from "@/service/pullNew.service";
import log2Sentry from "@/utils/log2Sentry";
import Observer from "@/containers/common/observer";
import clsx from "clsx";
import { getInviterWithInviteCode, getStockActivityInfo } from "@/service/activity.service";
import dayjs from "dayjs";
import HeadMetaContainer from "@/containers/common/HeadMetaContainer";
import useWxShare from "@/hooks/useWxShare";
import { ModulesMappings } from "@/interfaces/enums";
import { motion } from "framer-motion";
import useEventListener from "@/hooks/useEventListener";

interface Props {
	inviteCode: string;
	inviter: string;
	locale: string;
	isActivityActive: boolean;
	inviteeTotal: number;
	participateCount: number;
	qr?: string;
}

const MAX_REWARD_TIMES = 5;
export default function StockRebateInvited(props: Props) {
	const { t } = useTranslation("activities");
	const containerRef = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(true);

	const metaInfo = {
		title: t(`${ModulesMappings.stockRebate2024}-title`, {
			ns: "share",
			nickname: props.inviter,
		}),
		desc: t(`${ModulesMappings.stockRebate2024}-description`, { ns: "share" }),
		imgUrl: `${process.env.NEXT_PUBLIC_APP_OSS_PUBLIC_CND}/h5/logo.png`,
	};
	useWxShare(undefined, undefined, metaInfo);

	const [showHeaderBg, setShowHeaderBg] = useState(false);

	useEventListener(
		"scroll",
		(e) => {
			const scrollTop = containerRef.current?.scrollTop || 0;
			if (scrollTop > 150) {
				setVisible(false);
			} else {
				setVisible(true);
			}
		},
		containerRef,
	);

	const renderTopImg = () => {
		if (props.isActivityActive) {
			if (props.locale.includes("zh")) {
				return {
					src: `https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/songni-${props.locale}.png`,
					width: 362 * 2,
					height: 105 * 2,
				};
			}
			return {
				src: "https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/songni-en.png",
				width: 338.17 * 2,
				height: 88 * 2,
			};
		}

		if (props.locale.includes("zh")) {
			return {
				src: `https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/yaohaoyou-${props.locale}.png`,
				width: 337 * 2,
				height: 154 * 2,
			};
		}
		return {
			src: "https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/yaohaoyou-en.png",
			width: 343,
			height: 131,
		};
	};

	const renderOneMinuteImg = () => {
		if (props.locale.includes("zh")) {
			return {
				src: `https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/yifenzhong-${props.locale}.png`,
				width: 343 * 2,
				height: 57 * 2,
			};
		}
		return {
			src: "https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/yifenzhong-en.png",
			width: 339 * 2,
			height: 79.6 * 2,
		};
	};

	const renderCopyTradeImg = () => {
		if (props.locale.includes("zh")) {
			return {
				src: `https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/avd-2-${props.locale}.png`,
				width: 402 * 2,
				height: 166.5 * 2,
			};
		}
		return {
			src: "https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/avd-2-en.png",
			width: 402 * 2,
			height: 166.5 * 2,
		};
	};

	const handleDirectContactUs = () => {
		handleDirect("contact-us");
	};
	const handleDirectRule = () => {
		handleDirect("activities/stock-rebate/rule");
	};
	const handleDirectRegister = () => {
		handleDirect(`onboard/${props.inviteCode}`);
	};
	const handleDirectDownload = () => {
		handleDirect("intents/download?h=sr");
	};
	const handleDirect = (suffix: string) => {
		const _locale = props.locale === "zh-HK" ? "zh-Hant" : props.locale;

		window.location.href = `${window.location.origin}/${_locale}/${suffix}`;
	};

	return (
		<HeadMetaContainer title={metaInfo.title} description={metaInfo.desc} imageUrl={metaInfo.imgUrl}>
			<div
				className="relative mx-auto flex h-full w-full max-w-screen-sm flex-col text-white"
				style={{
					background: "linear-gradient(180deg, #3F2657 0%, #19007E 100%)",
				}}
			>
				<div
					className="hidden-scroll-bar relative flex-1 overflow-y-auto pb-[calc(131px+env(safe-area-inset-bottom))]"
					ref={containerRef}
				>
				<div className="relative pb-4">
					<HeroBg />
					<Observer
						onVisible={(val) => {
							setShowHeaderBg(!val);
						}}
					/>
					<div
						className={clsx("sticky left-0 top-0 z-[1000] pb-1 pt-5", showHeaderBg ? "bg-[#3F2657]" : "bg-transparent")}
					>
						<StockNavigation
							leadingNode={
								<Image
									src={getFullWebCdnUrl("/docs/options/logo-white.png")}
									width={166 / 1.5}
									height={57 / 1.5}
									alt="rockFlow"
									style={{
										maxWidth: "100%",
										height: "auto",
									}}
								/>
							}
							onRuleClick={handleDirectRule}
						/>
					</div>

					{props.isActivityActive ? (
						<p className="mt-[2vh] px-4 text-lg font-bold text-[#F9E18A]">{props.inviter}</p>
					) : null}

					<Observer
						onVisible={(val) => {
							setShowHeaderBg(!val);
						}}
					/>
					<div className="mt-2 px-4 text-left">
						<Image
							alt="hero-invite"
							{...renderTopImg()}
							style={{
								maxWidth: "100%",
								height: "auto",
							}}
						/>
					</div>

					<SpaceScene />

					{props.isActivityActive ? (
						<div className="-mt-4">
							<div className="relative z-[1000]">
								<div className="absolute left-1/2 top-0" style={{ transform: "translate(-70%, -140%)" }}>
									<motion.div
										initial={{ opacity: 1 }}
										animate={{ opacity: visible ? 1 : 0 }}
										transition={{ duration: 0.2 }}
									>
										<motion.div
											animate={{
												y: ["0%", "20%", "0%"],
											}}
											transition={{ duration: 1, repeat: Infinity }}
										>
											<Image
												src="https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/arrow.png"
												width={22}
												height={25}
												alt="arrow"
												style={{
													maxWidth: "100%",
													height: "auto",
												}}
											/>
										</motion.div>
									</motion.div>
								</div>
							</div>

							<a className="block px-4 text-left" id="one-minute" onClick={handleDirectRegister}>
								<Image
									alt="y_f_z"
									{...renderOneMinuteImg()}
									style={{
										maxWidth: "100%",
										height: "auto",
									}}
								/>
							</a>

							<MarqueeView
								locale={props.locale}
								participateCount={props.participateCount}
								onDownloadRockFlow={handleDirectDownload}
							/>

							<div className="mt-8">
								<AVDView
									title="Trade GPT"
									subTitle={t("aiDetectsOpportunities")}
									img={{
										src: "https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/avd-1.png",
										width: 375 * 2,
										height: 173 * 2,
										alt: "tradeGPT",
									}}
								/>

								<AVDView
									title={t("autoCopyTrading")}
									subTitle={t("monitorAndCopy")}
									img={{
										alt: "copyTrading",
										...renderCopyTradeImg(),
									}}
								/>

								<AVDView
									title={t("secureFunds")}
									subTitle={t("regulatedBroker")}
									img={{
										src: "https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/avd-3.png",
										width: 375 * 2,
										height: 180 * 2,
										alt: "tradeGPT",
									}}
								/>
							</div>
						</div>
					) : null}

					{props.isActivityActive ? null : <ContactUsFinished onClick={handleDirectContactUs} />}
				</div>
				</div>

				{props.isActivityActive ? (
					<InviteBtn
						id="register_and_claim_now"
						btnText={t("registerAndClaimNow")}
						topNode={
							<p className="px-6 text-center text-xs font-normal text-white">
								{Number(props.inviteeTotal) >= MAX_REWARD_TIMES
									? t("invitedLandingRewardSelf")
									: t("invitedLandingRewardBoth")}
							</p>
						}
						onClick={handleDirectRegister}
					/>
				) : null}
			</div>
		</HeadMetaContainer>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const locale = parseAcceptLang(context.req.headers);

	let inviter: string | undefined = "";
	let inviteCode: string | undefined = "";
	let isActivityActive = true;
	let inviteeTotal = 0;
	let participateCount = 0;

	// 邀请人信息
	try {
		if (context.query.inviteCode) {
			inviteCode = context.query.inviteCode as string;

			inviter = await PullNewServices.getInviter(inviteCode);
		}
	} catch (error) {
		if (context.query.inviteCode) {
			log2Sentry("通过邀请吗获取邀请人错误", "error", {
				inviteCode: context.query.inviteCode,
				error,
			});
		}
	}

	// 活动信息
	try {
		const data = await getStockActivityInfo();

		isActivityActive = dayjs(Date.now()).isBefore(dayjs(data?.endTime));
		if (!data.enabled) {
			isActivityActive = false;
		}
		participateCount = data.participateCount;
	} catch (error) {}

	// 邀请码对应的人数
	try {
		if (inviteCode) {
			const data = await getInviterWithInviteCode(inviteCode);
			inviteeTotal = data;
		}
	} catch (error) {}

	return {
		props: {
			...(await serverSideTranslations(locale, ["common", "activities", "share"])),
			locale: locale,
			...context.query,
			inviter: inviter || "Rocker",
			inviteCode,
			isActivityActive,
			inviteeTotal,
			participateCount,
		},
	};
};

StockRebateInvited.getLayout = function getLayout(page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};
